import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { useMedia } from "../hooks/useMedia";
import { ARTICLES, CATEGORIES } from "../data/articles";

export default function Learning() {
  const { url } = useMedia();
  const [cat, setCat] = useState("All");
  const filtered = cat === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === cat);

  return (
    <div className="min-h-screen" data-testid="learning-page">
      <PageHero
        eyebrow="Learning Center"
        title={<>The field <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>manual.</span></>}
        subtitle="Ten pillar articles, written by the operators. No filler. No SEO padding. What we actually tell property managers on the first walk-through."
        image={url("learning-hero") || url("outdoor-lighting")}
        kicker="Bookmark this page. Your incoming property manager will thank you."
      />

      {/* Filter chips */}
      <section className="py-14" style={{ background: "var(--bg)", borderBottom: "1px solid var(--line)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14 flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className="font-mono text-[10px] uppercase tracking-[0.36em] px-5 py-3 transition-all duration-500"
              style={{
                borderRadius: "var(--r-full)",
                border: `1px solid ${cat === c ? "var(--gold)" : "var(--line)"}`,
                background: cat === c ? "rgba(198,168,124,0.12)" : "transparent",
                color: cat === c ? "var(--gold)" : "var(--text-2)",
              }}
              data-testid={`learning-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              data-cursor="hover"
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Article grid */}
      <section className="py-24 md:py-28" style={{ background: "var(--bg)" }}>
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((a, i) => (
              <motion.article
                key={a.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.07 }}
                className="glass-card overflow-hidden group flex flex-col"
                data-testid={`article-card-${a.slug}`}
                data-cursor="hover"
              >
                <Link to={`/learn/${a.slug}`} className="flex flex-col flex-1">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {url(a.heroId) ? (
                      <img src={url(a.heroId)} alt="" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" draggable={false} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.36em]" style={{ background: "rgba(198,168,124,0.05)", color: "var(--text-3)" }}>Loading</div>
                    )}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.85) 100%)" }} />
                    <div className="absolute top-5 left-5 pill" style={{ background: "rgba(11,11,13,0.72)" }}>{a.category}</div>
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <h3 className="font-display text-2xl leading-[1.15]" style={{ letterSpacing: "-0.02em" }}>{a.title}</h3>
                    <p className="mt-5 font-body flex-1" style={{ color: "var(--text-2)", lineHeight: 1.65, fontSize: 15 }}>{a.excerpt}</p>
                    <div className="mt-8 pt-5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.36em]" style={{ borderTop: "1px solid var(--line)", color: "var(--gold)" }}>
                      <span>Read · {a.readTime}</span>
                      <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 md:py-36 text-center" style={{ background: "var(--bg-2)" }}>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl mx-auto px-6" style={{ letterSpacing: "-0.035em" }}>
          Ready when you <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>are.</span>
        </h2>
        <div className="mt-12 flex flex-wrap justify-center gap-5">
          <Link to="/quote" className="btn-glow" data-cursor="hover" data-testid="learning-cta-quote">
            Request a Quote <ArrowUpRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  );
}
